import { css, html } from 'lit';
import { flipAnimate } from '../../animation/flip.js';
import { BaseComponent } from '../../lib/base-component.js';
import { ABILITY_NAMES, ABILITY_ORDER } from '../../lib/constants.js';
import './option-card.js';

/** @typedef {import('../../lib/constants.js').AbilityKey} AbilityKey */

const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8];

/** @type {{[key: string]: Array<string>}} */
const PRIMARY_ABILITIES = {
  fighter: ['str', 'con'],
  rogue: ['dex'],
  cleric: ['wis'],
  wizard: ['int'],
  barbarian: ['str', 'con'],
  bard: ['cha'],
  druid: ['wis'],
  monk: ['dex', 'wis'],
  paladin: ['str', 'cha'],
  ranger: ['dex', 'wis'],
  sorcerer: ['cha'],
  warlock: ['cha'],
};

/** Total bonus budget: +2 costs 2, each +1 costs 1 */

class AbilityScoreAssigner extends BaseComponent {
  static properties = {
    abilityScores: { type: Object },
    classSlug: { type: String, attribute: 'class-slug' },
    backgroundAbilities: { type: Array },
    backgroundName: { type: String, attribute: 'background-name' },
    adjustmentMethod: { type: String, attribute: 'adjustment-method' },
    adjustmentPrimary: { type: String, attribute: 'adjustment-primary' },
    adjustmentSecondary: { type: String, attribute: 'adjustment-secondary' },
    recommendedScores: { type: Object },
    // internal
    _dragType: { state: true },
    _dragValue: { state: true },
    _dragSource: { state: true },
    _hoverTarget: { state: true },
    _bonusChoice: { state: true },
    _plusTwo: { state: true },
    _plusOnes: { state: true },
  };

  constructor() {
    super();
    this.abilityScores = { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 };
    this.classSlug = '';
    /** @type {string[]} */
    this.backgroundAbilities = [];
    this.backgroundName = '';
    this.adjustmentMethod = '';
    this.adjustmentPrimary = '';
    this.adjustmentSecondary = '';
    this.recommendedScores = null;
    this._dragType = null;
    this._dragValue = 0;
    this._dragSource = null;
    /** @type {string | null} */
    this._hoverTarget = null;
    /** @type {'' | 'split' | 'even'} user's chosen bonus method */
    this._bonusChoice = '';
    /** @type {string} ability key where +2 is placed, or '' */
    this._plusTwo = '';
    /** @type {Array<string>} ability keys where +1s are placed */
    this._plusOnes = [];
    /** @type {Array<Animation>} */
    this._activeAnimations = [];
    /** @type {boolean} flag to prevent re-sync after own emit */
    this._internalBonusChange = false;
  }

  static styles = css`
    :host {
      display: block;
    }

    .bank-slot {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 36px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 0.95rem;
      user-select: none;
    }
    .bank-slot.filled {
      background: var(--bs-primary);
      color: white;
      cursor: grab;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }
    .bank-slot.filled:active {
      cursor: grabbing;
    }
    .bank-slot.empty {
      border: 2px dashed var(--bs-gray-400);
      color: var(--bs-gray-400);
    }

    .ability-card {
      border: 1px solid var(--bs-border-color);
      border-radius: 8px;
      padding: 12px;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
      background: white;
    }
    .ability-card.drag-over {
      border-color: var(--bs-primary);
      box-shadow: 0 0 0 2px rgba(var(--bs-primary-rgb), 0.25);
    }
    .ability-card.drag-rejected {
      opacity: 0.5;
    }

    .drop-zone {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 36px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 0.95rem;
      user-select: none;
    }
    .drop-zone.occupied {
      background: var(--bs-primary);
      color: white;
      cursor: grab;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }
    .drop-zone.occupied:active {
      cursor: grabbing;
    }
    .drop-zone.vacant {
      border: 2px dashed var(--bs-gray-400);
    }
    .drop-zone.vacant.valid-target {
      border-color: var(--bs-primary);
      background: rgba(13, 110, 253, 0.08);
    }

    .bonus-drop-zone {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 36px;
      border-radius: 6px;
      border: 2px dashed var(--bs-primary);
      background: rgba(13, 110, 253, 0.08);
      font-weight: 700;
      font-size: 0.95rem;
      color: var(--bs-primary);
    }

    .bonus-chip {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 36px;
      border-radius: 6px;
      font-weight: 700;
      font-size: 0.95rem;
      user-select: none;
    }
    .bonus-chip.available {
      background: rgba(var(--bs-primary-rgb), 0.15);
      color: var(--bs-primary);
      cursor: grab;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
    .bonus-chip.available:active {
      cursor: grabbing;
    }
    .bonus-chip.placed {
      border: 2px dashed var(--bs-gray-400);
      color: var(--bs-gray-400);
    }
    .bonus-chip.locked {
      border: 2px dashed var(--bs-gray-300);
      color: var(--bs-gray-300);
    }

    .bonus-indicator {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 36px;
      border-radius: 6px;
      font-weight: 700;
      font-size: 0.95rem;
      background: rgba(var(--bs-primary-rgb), 0.15);
      color: var(--bs-primary);
      user-select: none;
    }
    .bonus-indicator.removable {
      cursor: grab;
    }
    .bonus-indicator.removable:active {
      cursor: grabbing;
    }

    .modifier-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
      padding: 2px 6px;
      border-radius: 4px;
      font-weight: 600;
      font-size: 0.85rem;
    }

    .score-equals {
      font-size: 0.95rem;
      color: var(--bs-secondary);
    }
    .final-score {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 36px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 0.95rem;
      border: 2px solid var(--bs-gray-400);
      color: var(--bs-gray-600);
    }
  `;

  // --- Bonus state sync from props ---

  /** @param {Map<string, any>} changedProperties */
  willUpdate(changedProperties) {
    if (
      changedProperties.has('adjustmentMethod') ||
      changedProperties.has('adjustmentPrimary') ||
      changedProperties.has('adjustmentSecondary')
    ) {
      if (this._internalBonusChange) {
        this._internalBonusChange = false;
      } else {
        this._syncBonusFromProps();
      }
    }
  }

  _syncBonusFromProps() {
    if (this.adjustmentMethod === 'split') {
      this._bonusChoice = 'split';
      this._plusTwo = this.adjustmentPrimary || '';
      this._plusOnes = this.adjustmentSecondary ? [this.adjustmentSecondary] : [];
    } else if (this.adjustmentMethod === 'even') {
      this._bonusChoice = 'even';
      this._plusTwo = '';
      this._plusOnes = [...this.backgroundAbilities];
    } else {
      this._bonusChoice = '';
      this._plusTwo = '';
      this._plusOnes = [];
    }
  }

  // --- Bonus computed getters ---

  get _bonusSpent() {
    return (this._plusTwo ? 2 : 0) + this._plusOnes.length;
  }

  // --- Computed getters ---

  get _assignedCount() {
    return Object.values(this.abilityScores).filter((v) => v > 0).length;
  }

  /** @param {AbilityKey} ability */
  _isPrimary(ability) {
    return PRIMARY_ABILITIES[this.classSlug]?.includes(ability) || false;
  }

  /** @param {AbilityKey} ability */
  _getAdjustment(ability) {
    if (ability === this._plusTwo) return 2;
    if (this._plusOnes.includes(ability)) return 1;
    return 0;
  }

  /** @param {AbilityKey} ability */
  _getFinal(ability) {
    const base = this.abilityScores[ability] || 0;
    return Math.min(20, base + this._getAdjustment(ability));
  }

  /** @param {number} score */
  _formatModifier(score) {
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  }

  /** @param {number} score */
  _getModifierClass(score) {
    const mod = Math.floor((score - 10) / 2);
    if (mod > 0) return 'bg-success text-white';
    if (mod < 0) return 'bg-danger text-white';
    return 'bg-light text-muted';
  }

  /** @param {number} value */
  _bankIndexForValue(value) {
    return STANDARD_ARRAY.indexOf(value);
  }

  /** @returns {Array<number>} */
  _getBankState() {
    const assignedValues = Object.values(this.abilityScores).filter((v) => v > 0);
    const remaining = [...assignedValues];
    return STANDARD_ARRAY.map((v) => {
      const idx = remaining.indexOf(v);
      if (idx >= 0) {
        remaining.splice(idx, 1);
        return 0;
      }
      return v;
    });
  }

  // --- Dispatch helpers ---

  /**
   * @param {{[key: string]: number}} scores
   * @param {string} [method]
   * @param {string} [primary]
   * @param {string} [secondary]
   */
  _emitChange(scores, method, primary, secondary) {
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          abilityScores: scores,
          adjustmentMethod: method ?? this.adjustmentMethod,
          adjustmentPrimary: primary ?? this.adjustmentPrimary,
          adjustmentSecondary: secondary ?? this.adjustmentSecondary,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Emit the current bonus state to the parent. Only emits complete configurations. */
  _emitBonusChange() {
    let method = '';
    let primary = '';
    let secondary = '';
    if (this._bonusChoice === 'split' && this._plusTwo && this._plusOnes.length === 1) {
      method = 'split';
      primary = this._plusTwo;
      secondary = this._plusOnes[0] || '';
    } else if (this._bonusChoice === 'even' && this._plusOnes.length === 3) {
      method = 'even';
    }
    this._internalBonusChange = true;
    this._emitChange(this.abilityScores, method, primary, secondary);
  }

  // --- Animation helpers ---

  _cancelAnimations() {
    for (const a of this._activeAnimations) {
      a.cancel();
    }
    this._activeAnimations = [];
  }

  /** @param {Element} el @param {DOMRect} fromRect */
  _flip(el, fromRect) {
    const anim = flipAnimate(el, fromRect);
    this._activeAnimations.push(anim);
    anim.finished
      .then(() => {
        this._activeAnimations = this._activeAnimations.filter((a) => a !== anim);
      })
      .catch(() => {});
  }

  // --- Element lookups ---

  /** @param {number} index */
  _getBankSlotEl(index) {
    return this.renderRoot.querySelector(`[data-bank-slot="${index}"]`);
  }

  /** @param {AbilityKey} ability */
  _getAbilitySlotEl(ability) {
    return this.renderRoot.querySelector(`[data-ability-slot="${ability}"]`);
  }

  // --- Hit detection ---

  /**
   * Find what drop target is at the given viewport coordinates.
   * @param {number} clientX
   * @param {number} clientY
   * @returns {{ type: 'ability', ability: string } | { type: 'bank' } | null}
   */
  _findDropTarget(clientX, clientY) {
    const cards = this.renderRoot.querySelectorAll('[data-ability]');
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
        return { type: 'ability', ability: /** @type {string} */ (/** @type {HTMLElement} */ (card).dataset.ability) };
      }
    }
    const bank = this.renderRoot.querySelector('[data-zone="bank"]');
    if (bank) {
      const rect = bank.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
        return { type: 'bank' };
      }
    }
    return null;
  }

  // --- Score pointer drag ---

  /**
   * @param {PointerEvent} e
   * @param {number} value
   * @param {string} source - 'bank' or ability key
   */
  _onScorePointerDown(e, value, source) {
    e.preventDefault();
    const target = /** @type {HTMLElement} */ (e.target);
    const startX = e.clientX;
    const startY = e.clientY;
    let dragging = false;
    /** @type {HTMLElement|null} */
    let ghost = null;

    const onMove = (/** @type {PointerEvent} */ moveE) => {
      if (!dragging) {
        if (Math.abs(moveE.clientX - startX) + Math.abs(moveE.clientY - startY) < 5) return;
        dragging = true;
        this._dragType = 'score';
        const rect = target.getBoundingClientRect();
        ghost = document.createElement('div');
        ghost.textContent = target.textContent;
        ghost.style.cssText = `
                    position: fixed; z-index: 9999; pointer-events: none;
                    width: ${rect.width}px; height: ${rect.height}px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 6px; font-weight: 600; font-size: 0.95rem;
                    background: rgb(13, 110, 253); color: white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2); opacity: 0.9;
                `;
        document.body.appendChild(ghost);
        target.style.opacity = '0';
      }
      if (ghost) {
        ghost.style.left = `${moveE.clientX - target.offsetWidth / 2}px`;
        ghost.style.top = `${moveE.clientY - target.offsetHeight / 2}px`;
      }
      // Hover highlighting
      const hit = this._findDropTarget(moveE.clientX, moveE.clientY);
      const newHover = hit?.type === 'ability' ? hit.ability : null;
      if (this._hoverTarget !== newHover) this._hoverTarget = newHover;
    };

    const onUp = (/** @type {PointerEvent} */ upE) => {
      cleanup();
      const ghostRect = ghost?.getBoundingClientRect() ?? null;
      if (ghost) {
        ghost.remove();
        ghost = null;
      }
      target.style.opacity = '';

      if (!dragging) return; // was just a click

      const dropTarget = this._findDropTarget(upE.clientX, upE.clientY);
      this._dragType = null;
      this._hoverTarget = null;

      if (dropTarget?.type === 'ability' && dropTarget.ability !== source) {
        this._handleScorePlacement(value, source, /** @type {AbilityKey} */ (dropTarget.ability));
      } else if (source !== 'bank' && (dropTarget?.type === 'bank' || dropTarget?.ability === source || !dropTarget)) {
        this._handleScoreReturn(value, /** @type {AbilityKey} */ (source), ghostRect);
      }
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /**
   * Place a score on an ability, handling displacement.
   * @param {number} value
   * @param {string} source
   * @param {AbilityKey} targetAbility
   */
  async _handleScorePlacement(value, source, targetAbility) {
    const scores = { ...this.abilityScores };
    const displacedValue = scores[targetAbility];

    let displacedRect = null;
    if (displacedValue > 0) {
      const el = this._getAbilitySlotEl(targetAbility);
      if (el) displacedRect = el.getBoundingClientRect();
    }

    scores[targetAbility] = value;
    if (source !== 'bank') {
      scores[/** @type {AbilityKey} */ (source)] = 0;
    }

    this._cancelAnimations();
    this._emitChange(scores, undefined, undefined, undefined);

    if (displacedValue > 0 && displacedRect) {
      await this.updateComplete;
      const bankIdx = this._bankIndexForValue(displacedValue);
      const bankEl = this._getBankSlotEl(bankIdx);
      if (bankEl) this._flip(bankEl, displacedRect);
    }
  }

  /**
   * Return a score from an ability to the bank.
   * @param {number} value
   * @param {AbilityKey} source
   * @param {DOMRect|null} fromRect - ghost position to animate from
   */
  async _handleScoreReturn(value, source, fromRect) {
    const scores = { ...this.abilityScores };
    scores[source] = 0;

    this._cancelAnimations();
    this._emitChange(scores, undefined, undefined, undefined);

    if (fromRect) {
      await this.updateComplete;
      const bankIdx = this._bankIndexForValue(value);
      const bankEl = this._getBankSlotEl(bankIdx);
      if (bankEl) this._flip(bankEl, fromRect);
    }
  }

  /** @param {DragEvent} e */
  _onDragEnd(e) {
    /** @type {HTMLElement} */ (e.target).style.opacity = '';
    this._resetDrag();
  }

  _resetDrag() {
    this._dragType = null;
    this._dragValue = 0;
    this._dragSource = null;
    this._hoverTarget = null;
  }

  // --- Placed bonus removal (pointer events) ---

  /**
   * @param {PointerEvent} e
   * @param {number} bonusValue
   * @param {AbilityKey} ability
   */
  _onBonusPointerDown(e, bonusValue, ability) {
    e.preventDefault();
    const target = /** @type {HTMLElement} */ (e.target);
    const startX = e.clientX;
    const startY = e.clientY;
    let dragging = false;
    /** @type {HTMLElement|null} */
    let ghost = null;

    const onMove = (/** @type {PointerEvent} */ moveE) => {
      if (!dragging) {
        if (Math.abs(moveE.clientX - startX) + Math.abs(moveE.clientY - startY) < 5) return;
        dragging = true;
        const rect = target.getBoundingClientRect();
        ghost = document.createElement('div');
        ghost.textContent = target.textContent;
        ghost.style.cssText = `
                    position: fixed; z-index: 9999; pointer-events: none;
                    width: ${rect.width}px; height: ${rect.height}px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 6px; font-weight: 700; font-size: 0.95rem;
                    background: rgba(13, 110, 253, 0.15); color: rgb(13, 110, 253);
                    opacity: 0.85;
                `;
        document.body.appendChild(ghost);
        target.style.opacity = '0';
      }
      if (ghost) {
        ghost.style.left = `${moveE.clientX - target.offsetWidth / 2}px`;
        ghost.style.top = `${moveE.clientY - target.offsetHeight / 2}px`;
      }
    };

    const onUp = () => {
      cleanup();
      const fromRect = ghost ? ghost.getBoundingClientRect() : target.getBoundingClientRect();
      if (ghost) {
        ghost.remove();
        ghost = null;
      }
      target.style.opacity = '';
      this._removePlacedBonus(bonusValue, ability, fromRect);
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  /**
   * @param {number} bonusValue
   * @param {AbilityKey} ability
   * @param {DOMRect} fromRect
   */
  async _removePlacedBonus(bonusValue, ability, fromRect) {
    if (bonusValue === 2) {
      this._plusTwo = '';
    } else {
      this._plusOnes = this._plusOnes.filter((a) => a !== ability);
    }

    this._cancelAnimations();
    this._emitBonusChange();

    await this.updateComplete;
    await new Promise((r) => requestAnimationFrame(r));
    await this.updateComplete;

    // Find the bank chip that just became available
    const bankChips = this.renderRoot.querySelectorAll('.bonus-chip');
    const targetChip = Array.from(bankChips).find(
      (c) => c.classList.contains('available') && c.textContent.trim() === `+${bonusValue}`,
    );
    if (targetChip) this._flip(targetChip, fromRect);
  }

  /** @param {'split' | 'even'} choice */
  _onBonusChoiceSelected(choice) {
    if (choice === this._bonusChoice) return;
    this._bonusChoice = choice;
    this._plusTwo = '';
    this._plusOnes = [];
    if (choice === 'even') {
      this._plusOnes = [...this.backgroundAbilities];
      this._emitBonusChange();
    } else {
      // split: user needs to drag +2 and +1, emit cleared state for now
      this._emitBonusChange();
    }
  }

  // --- Bonus drag handlers (bank chips only, HTML5 DnD) ---

  /** @param {DragEvent} e @param {number} bonusValue */
  _onBonusDragStart(e, bonusValue) {
    this._dragType = 'bonus';
    this._dragValue = bonusValue;
    this._dragSource = 'bank';
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `+${bonusValue}`);
    }
  }

  /** @param {DragEvent} e @param {AbilityKey} ability */
  _onBonusDragOver(e, ability) {
    if (this._dragType !== 'bonus') return;
    if (!this.backgroundAbilities.includes(ability)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    this._hoverTarget = ability;
  }

  /** @param {DragEvent} e @param {AbilityKey} ability */
  async _onBonusDrop(e, ability) {
    e.preventDefault();
    if (this._dragType !== 'bonus') return;
    if (!this.backgroundAbilities.includes(ability)) return;

    const bonusValue = this._dragValue;

    // Capture rect of displaced indicator before state change
    const indicatorEl = this.renderRoot
      .querySelector(`[data-ability-slot="${ability}"]`)
      ?.parentElement?.querySelector('.bonus-indicator');
    const displacedRect = indicatorEl?.getBoundingClientRect() ?? null;
    const displacedValue = this._getAdjustment(ability);

    if (bonusValue === 2) {
      // Displace existing +1 if present
      if (this._plusOnes.includes(ability)) {
        this._plusOnes = this._plusOnes.filter((a) => a !== ability);
      }
      this._plusTwo = ability;
    } else {
      // Displace existing +2 if present
      if (this._plusTwo === ability) {
        this._plusTwo = '';
      }
      if (!this._plusOnes.includes(ability)) {
        this._plusOnes = [...this._plusOnes, ability];
      }
    }

    this._emitBonusChange();
    this._resetDrag();

    // Animate displaced bonus back to bank
    if (displacedValue > 0 && displacedRect) {
      await this.updateComplete;
      await new Promise((r) => requestAnimationFrame(r));
      await this.updateComplete;

      const bankChips = this.renderRoot.querySelectorAll('.bonus-chip');
      const targetChip = Array.from(bankChips).find(
        (c) => c.classList.contains('available') && c.textContent.trim() === `+${displacedValue}`,
      );
      if (targetChip) this._flip(targetChip, displacedRect);
    }
  }

  // --- Button actions ---

  async _clearAll() {
    const rects = this._captureAllChipRects();

    // Clear bonus state
    this._bonusChoice = '';
    this._plusTwo = '';
    this._plusOnes = [];

    this._cancelAnimations();
    this._internalBonusChange = true;
    this._emitChange({ str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 }, '', '', '');

    await this.updateComplete;
    this._animateAllChips(rects);
  }

  /** @returns {Map<number, DOMRect>} */
  _captureAllChipRects() {
    /** @type {Map<number, DOMRect>} */
    const rects = new Map();
    const bankState = this._getBankState();
    for (let i = 0; i < STANDARD_ARRAY.length; i++) {
      if ((bankState[i] || 0) > 0) {
        const el = this._getBankSlotEl(i);
        if (el) rects.set(/** @type {number} */ (bankState[i]), el.getBoundingClientRect());
      }
    }
    for (const ability of ABILITY_ORDER) {
      const val = this.abilityScores[ability];
      if (val > 0) {
        const el = this._getAbilitySlotEl(ability);
        if (el) rects.set(val, el.getBoundingClientRect());
      }
    }
    return rects;
  }

  /** @param {Map<number, DOMRect>} oldRects */
  _animateAllChips(oldRects) {
    const newBankState = this._getBankState();
    for (let i = 0; i < STANDARD_ARRAY.length; i++) {
      const val = /** @type {number} */ (newBankState[i]);
      if (val > 0 && oldRects.has(val)) {
        const el = this._getBankSlotEl(i);
        if (el) this._flip(el, /** @type {DOMRect} */ (oldRects.get(val)));
      }
    }
    for (const ability of ABILITY_ORDER) {
      const val = this.abilityScores[ability];
      if (val > 0 && oldRects.has(val)) {
        const el = this._getAbilitySlotEl(ability);
        if (el) this._flip(el, /** @type {DOMRect} */ (oldRects.get(val)));
      }
    }
  }

  // --- Render ---

  render() {
    const bankState = this._getBankState();

    return html`
      <div>
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">Choose Your Ability Scores</h5>
        </div>

        <p class="text-muted small mb-3">Drag scores into abilities.</p>

        <!-- Score Bank -->
        <div class="mb-3 d-flex align-items-center flex-wrap gap-2" data-zone="bank">
          <span class="text-muted small me-1">Scores:</span>
          ${bankState.map(
            (value, index) => html`
              <div
                class="bank-slot ${value > 0 ? 'filled' : 'empty'}"
                data-bank-slot="${index}"
                @pointerdown="${
                  value > 0 ? (/** @type {PointerEvent} */ e) => this._onScorePointerDown(e, value, 'bank') : null
                }"
              >
                ${value > 0 ? value : STANDARD_ARRAY[index]}
              </div>
            `,
          )}
          ${
            this._assignedCount > 0 || this._bonusSpent > 0
              ? html`
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary text-nowrap ms-2"
                  @click="${this._clearAll}"
                >
                  Reset
                </button>
              `
              : ''
          }
        </div>

        <!-- Ability cards grid -->
        <div class="row g-3 mb-4">${ABILITY_ORDER.map((ability) => this._renderAbilityCard(ability))}</div>

        <!-- Background Bonus Section -->
        ${
          this.backgroundAbilities.length > 0
            ? html`
              <h6 class="mb-2">Background Adjustment${this.backgroundName ? ` (${this.backgroundName})` : ''}</h6>
              <p class="text-muted small mb-3">
                Your background lets you boost: ${this.backgroundAbilities.map((a) => ABILITY_NAMES[a]).join(', ')}.
              </p>

              <div class="row g-2 mb-3">
                <div class="col-6">
                  <div class="row g-2">
                    <div class="col-6">
                      <option-card
                        name="+2 / +1"
                        slug="split"
                        description="Assign +2 to one and +1 to another"
                        .selected="${this._bonusChoice === 'split'}"
                        radio-group="bonus-method"
                        @card-selected="${() => this._onBonusChoiceSelected('split')}"
                      ></option-card>
                    </div>
                    <div class="col-6">
                      <option-card
                        name="+1 to all three"
                        slug="even"
                        description="Apply +1 to each background ability"
                        .selected="${this._bonusChoice === 'even'}"
                        radio-group="bonus-method"
                        @card-selected="${() => this._onBonusChoiceSelected('even')}"
                      ></option-card>
                    </div>
                  </div>
                </div>
              </div>

              ${
                this._bonusChoice === 'split'
                  ? html`
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-muted small">Bonuses:</span>
                      ${this._renderBonusChip(2)} ${this._renderBonusChip(1)}
                    </div>
                  `
                  : ''
              }
            `
            : ''
        }
      </div>
    `;
  }

  /**
   * Render a single bonus bank chip.
   * @param {number} value - 2 or 1
   */
  _renderBonusChip(value) {
    const placed = value === 2 ? !!this._plusTwo : this._plusOnes.length > 0;
    const available = !placed;
    const state = placed ? 'placed' : 'available';
    return html`
      <div
        class="bonus-chip ${state}"
        draggable="${available ? 'true' : 'false'}"
        @dragstart="${available ? (/** @type {DragEvent} */ e) => this._onBonusDragStart(e, value) : null}"
        @dragend="${this._onDragEnd}"
      >
        +${value}
      </div>
    `;
  }

  /** @param {AbilityKey} ability */
  _renderAbilityCard(ability) {
    const baseScore = this.abilityScores[ability];
    const adj = this._getAdjustment(ability);
    const final = this._getFinal(ability);
    const isPrimary = this._isPrimary(ability);
    const isHovered = this._hoverTarget === ability;
    const isBgAbility = this.backgroundAbilities.includes(ability);
    const isRejected = this._dragType === 'bonus' && !isBgAbility;
    const showDragOver = isHovered && (this._dragType === 'score' || (this._dragType === 'bonus' && isBgAbility));

    return html`
      <div class="col-12 col-md-4">
        <div
          class="ability-card ${isPrimary ? 'primary' : ''} ${showDragOver ? 'drag-over' : ''} ${
            isRejected ? 'drag-rejected' : ''
          }"
          data-ability="${ability}"
          @dragover="${(/** @type {DragEvent} */ e) => {
            if (this._dragType === 'bonus') this._onBonusDragOver(e, ability);
          }}"
          @dragleave="${() => {
            if (this._hoverTarget === ability) this._hoverTarget = null;
          }}"
          @drop="${(/** @type {DragEvent} */ e) => {
            if (this._dragType === 'bonus') this._onBonusDrop(e, ability);
          }}"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
              <span class="fw-medium">${ABILITY_NAMES[ability]}</span>
              ${isPrimary ? html`<span class="badge bg-primary ms-2">Primary</span>` : ''}
            </div>
            ${
              baseScore > 0
                ? html`
                  <span class="modifier-badge ${this._getModifierClass(final)}"> ${this._formatModifier(final)} </span>
                `
                : html` <span class="modifier-badge bg-light text-muted">--</span> `
            }
          </div>
          <div class="d-flex align-items-center gap-2">
            <div
              class="drop-zone ${baseScore > 0 ? 'occupied' : 'vacant'}${
                baseScore === 0 && this._dragType === 'score' ? ' valid-target' : ''
              }"
              data-ability-slot="${ability}"
              @pointerdown="${
                baseScore > 0
                  ? (/** @type {PointerEvent} */ e) => this._onScorePointerDown(e, baseScore, ability)
                  : null
              }"
            >
              ${baseScore > 0 ? baseScore : ''}
            </div>
            ${
              adj > 0
                ? html`
                  <div
                    class="bonus-indicator ${this._bonusChoice === 'split' ? 'removable' : ''}"
                    @pointerdown="${
                      this._bonusChoice === 'split'
                        ? (/** @type {PointerEvent} */ e) => this._onBonusPointerDown(e, adj, ability)
                        : null
                    }"
                  >
                    +${adj}
                  </div>
                `
                : this._dragType === 'bonus' && isBgAbility
                  ? html` <div class="bonus-drop-zone">+</div> `
                  : ''
            }
            ${
              baseScore > 0 && adj > 0
                ? html`
                  <span class="score-equals">=</span>
                  <div class="final-score">${final}</div>
                `
                : ''
            }
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('ability-score-assigner', AbilityScoreAssigner);
